import { Button, useClickOutside, useDropdownDirection } from '@consigli/facade';
import { type ProjectParticipant, type ResponsibleParty } from '@consigli/types';
import { FC, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { getMeetingId, type SupportedItem } from './action-item-types';
import { ReassignPersonPopover, type ReassignResultItem } from './reassign-person-popover';
import { useActionItemMutations } from '../hooks/use-action-item-mutations';
import { resolveResponsibleParty } from '../utils/participant-utils';

interface ReassignDropdownProps {
  item: SupportedItem;
  meetingId?: string;
  participants: ProjectParticipant[];
  customParties?: ResponsibleParty[];
  onUpdate?: (updated: SupportedItem) => void;
}

export const ReassignDropdown: FC<ReassignDropdownProps> = ({
  item,
  meetingId,
  participants,
  customParties,
  onUpdate,
}) => {
  const { t } = useTranslation();

  const resolvedMeetingId = getMeetingId(item, meetingId);
  const { updateItem } = useActionItemMutations(resolvedMeetingId);

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const openDirection = useDropdownDirection({
    containerRef,
    isOpen,
    dropdownHeight: 360,
  });

  const closePopover = useCallback(() => {
    if (isSaving) return;
    setIsOpen(false);
  }, [isSaving]);

  useClickOutside({ ref: containerRef, onClickOutside: closePopover, enabled: isOpen });

  const handleSave = useCallback(
    async (result: ReassignResultItem[]) => {
      if (!item.id) {
        setIsOpen(false);
        return;
      }

      // An empty result means "unassign". The backend clears the owner as long
      // as the assignment keys are present in the payload (both sent as null);
      // omitting them would leave the current owner untouched.
      const target = result[0];

      setIsSaving(true);
      try {
        await updateItem(item.id, {
          assignedMeetingUserId: target?.meetingUserId ?? null,
          assignedCustomPartyName: target?.customPartyName ?? null,
          descriptionText: item.descriptionText,
        });

        const responsiblePerson = target ? resolveResponsibleParty(target, participants) : null;
        onUpdate?.({ ...item, responsiblePerson } as SupportedItem);
        toast.success(t('meeting-secretary.action-items.item-reassigned'));
        setIsOpen(false);
      } catch {
        toast.error(t('meeting-secretary.meetings.error-reassigning-item'));
      } finally {
        setIsSaving(false);
      }
    },
    [item, participants, updateItem, onUpdate, t],
  );

  const selected = item.responsiblePerson
    ? [
        {
          meetingUserId: item.responsiblePerson.meetingUserId,
          customPartyId: item.responsiblePerson.customPartyId,
          name: item.responsiblePerson.name,
        },
      ]
    : [];

  return (
    <div className="relative" ref={containerRef}>
      <Button
        tertiary
        iconOnly
        icon={FaUserPlus}
        iconSize={14}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        className="!p-2 rounded-full text-gray-400 hover:text-brand hover:bg-day-light-3 transition-colors"
        title={t('meeting-secretary.meetings.delegate-to-attendee')}
        disabled={isSaving}
      />

      {isOpen && (
        <ReassignPersonPopover
          title={t('meeting-secretary.meetings.reassign-to')}
          participants={participants}
          mode="single"
          allowCustom
          align="right"
          selected={selected}
          customParties={customParties}
          isSaving={isSaving}
          direction={openDirection}
          onSave={handleSave}
          onClose={closePopover}
        />
      )}
    </div>
  );
};
